/**
 * Cross-browser fullscreen helpers for the interview "kiosk" mode.
 *
 * Browsers only allow entering fullscreen from a user gesture (click/keydown)
 * and never let a page block Esc from exiting — so callers must invoke
 * `enterFullscreen` inside an event handler, detect exits via the
 * `fullscreenchange` event, and re-enter only on another gesture.
 */

type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

export function isFullscreen(): boolean {
  if (typeof document === "undefined") return false;
  const d = document as FsDocument;
  return Boolean(d.fullscreenElement || d.webkitFullscreenElement);
}

export async function enterFullscreen(
  target?: HTMLElement | null,
): Promise<boolean> {
  if (typeof document === "undefined") return false;
  const el = (target || document.documentElement) as FsElement;
  const req = el.requestFullscreen || el.webkitRequestFullscreen;
  if (!req) return false;
  try {
    await req.call(el);
    return true;
  } catch (_) {
    // Rejected (e.g. not a user gesture, or permission denied). Caller decides.
    return false;
  }
}

export async function exitFullscreen(): Promise<void> {
  if (typeof document === "undefined" || !isFullscreen()) return;
  const d = document as FsDocument;
  const exit = d.exitFullscreen || d.webkitExitFullscreen;
  if (!exit) return;
  try {
    await exit.call(d);
  } catch (_) {
    /* already exiting / not permitted — nothing else we can do */
  }
}

/** Phones/tablets used for candidate interviews — desktop/laptop unchanged. */
export function isMobileInterviewDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  return navigator.maxTouchPoints > 1 && window.innerWidth < 1024;
}

/** True on iPhone / iPad / iPod — used for platform-specific UX copy. */
export function isIOSDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

/** Whether the Fullscreen API is available (false on iOS Safari for normal pages). */
export function isFullscreenApiSupported(): boolean {
  if (typeof document === "undefined") return false;
  const el = document.documentElement as FsElement;
  return Boolean(el.requestFullscreen || el.webkitRequestFullscreen);
}
